import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Percent, ArrowLeft, Save, User, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const DiscountManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [ownerDiscounts, setOwnerDiscounts] = useState([]);
  const [bookOwners, setBookOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOwnerDiscountDialog, setShowOwnerDiscountDialog] = useState(false);
  const [newOwnerDiscount, setNewOwnerDiscount] = useState({ owner_id: '', discount_percentage: '' });
  const [stats, setStats] = useState({
    total_categories: 0,
    categories_with_discount: 0,
    avg_discount: 0,
    owners_with_discount: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, ownerDiscountsRes, ownersRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/owner-discounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/book-owners`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCategories(categoriesRes.data);
      setOwnerDiscounts(ownerDiscountsRes.data);
      setBookOwners(ownersRes.data);
      
      const withDiscount = categoriesRes.data.filter(c => c.discount_percentage > 0).length;
      const avgDiscount = categoriesRes.data.length > 0 
        ? categoriesRes.data.reduce((sum, c) => sum + c.discount_percentage, 0) / categoriesRes.data.length 
        : 0;
      
      setStats({
        total_categories: categoriesRes.data.length,
        categories_with_discount: withDiscount,
        avg_discount: avgDiscount,
        owners_with_discount: ownerDiscountsRes.data.length
      });
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategoryDiscount = async (categoryId, newDiscount) => {
    try {
      await axios.put(
        `${API}/categories/${categoryId}/discount`,
        { discount_percentage: parseFloat(newDiscount) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Discount updated to ${newDiscount}%`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update discount');
    }
  };

  const handleDiscountChange = (categoryId, value) => {
    setCategories(categories.map(cat => 
      cat.id === categoryId 
        ? { ...cat, discount_percentage: parseFloat(value) || 0 }
        : cat
    ));
  };

  const handleCreateOwnerDiscount = async (e) => {
    e.preventDefault();
    
    if (!newOwnerDiscount.owner_id || !newOwnerDiscount.discount_percentage) {
      toast.error('Please select an owner and enter a discount percentage');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/owner-discounts`,
        {
          owner_id: newOwnerDiscount.owner_id,
          discount_percentage: parseFloat(newOwnerDiscount.discount_percentage)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Owner discount created');
      setShowOwnerDiscountDialog(false);
      setNewOwnerDiscount({ owner_id: '', discount_percentage: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create owner discount');
    }
  };

  const handleDeleteOwnerDiscount = async (ownerId) => {
    if (!window.confirm('Are you sure you want to delete this owner discount?')) return;
    
    try {
      await axios.delete(`${API}/admin/owner-discounts/${ownerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Owner discount deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete owner discount');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/super-admin')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Discount Management</h1>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Percent className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Categories</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_categories}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Percent className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Categories with Discount</p>
            <p className="text-3xl font-bold text-gray-900">{stats.categories_with_discount}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Percent className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Average Discount</p>
            <p className="text-3xl font-bold text-gray-900">{stats.avg_discount.toFixed(1)}%</p>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <User className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Owners with Discount</p>
            <p className="text-3xl font-bold text-gray-900">{stats.owners_with_discount}</p>
          </div>
        </div>

        <Tabs defaultValue="categories" className="w-full">
          <TabsList className="mb-6" style={{backgroundColor: 'white', padding: '4px'}}>
            <TabsTrigger value="categories" style={{color: '#A89968'}}>Category Discounts</TabsTrigger>
            <TabsTrigger value="owners" style={{color: '#A89968'}}>Book Owner Discounts</TabsTrigger>
          </TabsList>

          {/* Category Discounts Tab */}
          <TabsContent value="categories">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>Category Discounts</h2>
                <p className="text-sm text-gray-600 mt-1">Configure discount percentages for each category</p>
              </div>

              {loading ? (
                <div className="py-12 text-center">Loading...</div>
              ) : (
                <div className="space-y-4">
                  {categories.map((category) => (
                    <div 
                      key={category.id} 
                      className="flex items-center justify-between border rounded-lg p-6"
                      style={{borderColor: '#C19A84'}}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                          <span className="text-3xl">{category.icon}</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg" style={{color: 'var(--foreground)'}}>{category.name}</h3>
                          <p className="text-sm text-gray-600">Set discount for this category</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={category.discount_percentage}
                            onChange={(e) => handleDiscountChange(category.id, e.target.value)}
                            className="w-24 text-center"
                          />
                          <Percent className="h-5 w-5 text-gray-500" />
                        </div>
                        <Button
                          onClick={() => handleUpdateCategoryDiscount(category.id, category.discount_percentage)}
                          className="hover:opacity-90"
                          style={{backgroundColor: 'var(--primary)', color: 'white'}}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Owner Discounts Tab */}
          <TabsContent value="owners">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold" style={{color: 'var(--foreground)'}}>Book Owner Discounts</h2>
                  <p className="text-sm text-gray-600 mt-1">Configure special discounts for specific book owners</p>
                </div>
                <Dialog open={showOwnerDiscountDialog} onOpenChange={setShowOwnerDiscountDialog}>
                  <DialogTrigger asChild>
                    <Button style={{backgroundColor: 'var(--primary)', color: 'white'}}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Owner Discount
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle style={{color: 'var(--foreground)'}}>Add Owner Discount</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateOwnerDiscount} className="space-y-4">
                      <div>
                        <Label style={{color: 'var(--foreground)'}}>Select Book Owner</Label>
                        <Select 
                          value={newOwnerDiscount.owner_id} 
                          onValueChange={(value) => setNewOwnerDiscount({...newOwnerDiscount, owner_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select an owner" />
                          </SelectTrigger>
                          <SelectContent>
                            {bookOwners.map((owner) => (
                              <SelectItem key={owner.id} value={owner.id}>
                                {owner.name} ({owner.book_count} books)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label style={{color: 'var(--foreground)'}}>Discount Percentage</Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={newOwnerDiscount.discount_percentage}
                            onChange={(e) => setNewOwnerDiscount({...newOwnerDiscount, discount_percentage: e.target.value})}
                            placeholder="e.g., 10"
                          />
                          <Percent className="h-5 w-5 text-gray-500" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full" style={{backgroundColor: 'var(--primary)', color: 'white'}}>
                        Create Discount
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="py-12 text-center">Loading...</div>
              ) : ownerDiscounts.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No owner discounts configured yet</p>
                  <p className="text-sm">Click "Add Owner Discount" to create one</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ownerDiscounts.map((discount) => (
                    <div 
                      key={discount.id} 
                      className="flex items-center justify-between border rounded-lg p-6"
                      style={{borderColor: 'var(--accent)'}}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                          <User className="h-6 w-6" style={{color: '#A89968'}} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg" style={{color: 'var(--foreground)'}}>{discount.owner_name}</h3>
                          <p className="text-sm text-gray-600">Owner ID: {discount.owner_id.slice(0, 8)}...</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="px-4 py-2 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                          <span className="font-bold text-lg" style={{color: 'var(--accent)'}}>
                            {discount.discount_percentage}% OFF
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteOwnerDiscount(discount.owner_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DiscountManagement;
